import { useState, useCallback, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Toast } from 'react-vant'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import FixedBottom from '@/components/FixedBottom'
import CustomButton from '@/components/CustomButton'
import SkeletonBox from '@/components/SkeletonBox'
import { APIWeb } from '@/api'
import KycPersonalInfoForm from './components/KycPersonalInfoForm'
import KycDocumentUploadForm from './components/KycDocumentUploadForm'
import { 
  KycMode, 
  type KycFormData, 
  convertDetailToFormData, 
  isAllFormValid,
  convertFormDataToApiRequest,
  initialFormData
} from './types'

type TabType = 'personal' | 'document'

export default function EditKyc() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState<TabType>('personal')
  const [formData, setFormData] = useState<KycFormData>(initialFormData)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(!!location.state?.kycId)
  const [kycId, setKycId] = useState<number | null>(null)

  const [failReason, setFailReason] = useState<string>()

  // 是否为KYC失败重提交流程（虚拟卡/实体卡均会传此标志）
  const isReapply = location.state?.isReapply === true
  const applicationId = location.state?.applicationId as number | undefined

  // 从路由state获取kycId并请求详情
  useEffect(() => {
    const id = location.state?.kycId
    if (id) {
      setKycId(id)
      fetchKycDetail(id)
    }
  }, [location.state])

  // 获取KYC详情
  const fetchKycDetail = async (id: number) => {
    try {
      setLoading(true)
      const response = await APIWeb.kycDetail({ id })
      if (response?.kyc) {
        setFormData(convertDetailToFormData(response.kyc))
        if (response.kyc.fail_reason) {
          setFailReason(response.kyc.fail_reason)
        }
      }
    } catch (error) {
      console.error('获取KYC详情失败:', error)
      Toast.fail(t('获取数据失败'))
    } finally {
      setLoading(false)
    }
  }

  // 更新表单数据
  const updateFormData = useCallback((data: Partial<KycFormData>) => {
    setFormData(prev => ({ ...prev, ...data }))
  }, [])

  // 返回上一页
  const handleBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  // 检查表单是否可提交
  const canSubmit = useMemo(() => isAllFormValid(formData) && kycId !== null, [formData, kycId])

  // 提交保存
  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !kycId) return

    try {
      setSubmitting(true)
      const requestData = convertFormDataToApiRequest(formData)
      await APIWeb.kycUpdate({ ...requestData, id: kycId })

      // KYC失败后重新提交（虚拟卡/实体卡）：kycUpdate 成功后必须 reapply 也成功才算完成
      if (isReapply) {
        if (!applicationId) {
          throw new Error(t('缺少申请ID无法重新提交'))
        }
        try {
          await APIWeb.reapplyCardKycV2({
            application_id: applicationId,
            kyc_id: kycId
          })
        } catch (reapplyError: any) {
          console.error('重新提交申请失败:', reapplyError)
          Toast.fail(reapplyError?.message || t('重新提交申请失败请稍后重试'))
          return
        }
      }

      Toast.success(t('保存成功'))
      navigate(-1)
    } catch (error: any) {
      console.error('KYC更新失败:', error)
      Toast.fail(error?.message || t('保存失败请稍后重试'))
    } finally {
      setSubmitting(false)
    }
  }, [formData, canSubmit, kycId, isReapply, applicationId, navigate, t])

  return (
    <PageLayout>
      <div className="flex flex-col h-full">
        <Header
          leftChildren={<Back onClick={handleBack} />}
          centerChildren={<span className="text-base font-medium">{t('KYC信息')}</span>}
        />

        {/* KYC失败原因提示 */}
        {failReason && (
          <div className="mx-5 mt-4 px-4 py-3 bg-[#FDECED] rounded-xl flex items-center">
            <img src="/images/card-v2/warning-tips.svg" alt="" className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="text-sm text-[#F5222D]">{failReason}</span>
          </div>
        )}

        {/* Tab切换 */}
        <div className="flex px-5 pt-4 gap-2">
          <button
            onClick={() => setActiveTab('personal')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all border-none cursor-pointer ${
              activeTab === 'personal'
                ? 'bg-[var(--primary-color)] text-white'
                : 'bg-[#F2F3F5] text-[#86909C]'
            }`}
          >
            {t('个人信息')}
          </button>
          <button
            onClick={() => setActiveTab('document')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all border-none cursor-pointer ${
              activeTab === 'document'
                ? 'bg-[var(--primary-color)] text-white'
                : 'bg-[#F2F3F5] text-[#86909C]'
            }`}
          >
            {t('证件信息')}
          </button>
        </div>

        <div className="flex-1 px-5 pt-6 pb-24 overflow-y-auto">
          {loading ? (
            <div className="space-y-4">
              {/* 国家/地区 */}
              <div>
                <SkeletonBox className="h-4 w-20 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 姓氏 & 名字 */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <SkeletonBox className="h-4 w-12 rounded mb-2" />
                  <SkeletonBox className="h-12 rounded-2.5" />
                </div>
                <div className="flex-1">
                  <SkeletonBox className="h-4 w-12 rounded mb-2" />
                  <SkeletonBox className="h-12 rounded-2.5" />
                </div>
              </div>
              {/* 性别 */}
              <div>
                <SkeletonBox className="h-4 w-12 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 手机号 */}
              <div>
                <SkeletonBox className="h-4 w-16 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 电子邮箱 */}
              <div>
                <SkeletonBox className="h-4 w-20 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 国籍 */}
              <div>
                <SkeletonBox className="h-4 w-32 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 出生日期 */}
              <div>
                <SkeletonBox className="h-4 w-24 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 职务 */}
              <div>
                <SkeletonBox className="h-4 w-12 rounded mb-2" />
                <SkeletonBox className="h-12 rounded-2.5" />
              </div>
              {/* 省份 & 城市 */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <SkeletonBox className="h-4 w-12 rounded mb-2" />
                  <SkeletonBox className="h-12 rounded-2.5" />
                </div>
                <div className="flex-1">
                  <SkeletonBox className="h-4 w-12 rounded mb-2" />
                  <SkeletonBox className="h-12 rounded-2.5" />
                </div>
              </div>
              {/* 详细地址 */}
              <div>
                <SkeletonBox className="h-4 w-20 rounded mb-2" />
                <SkeletonBox className="h-24 rounded-2.5" />
              </div>
            </div>
          ) : activeTab === 'personal' ? (
            <KycPersonalInfoForm
              formData={formData}
              updateFormData={updateFormData}
              readonly={false}
            />
          ) : (
            <KycDocumentUploadForm
              formData={formData}
              updateFormData={updateFormData}
              readonly={false}
              showHandheld={true}
            />
          )}
        </div>

        {/* 底部保存按钮 */}
        <FixedBottom className="bg-white">
          <CustomButton onClick={handleSubmit} loading={submitting} disabled={!canSubmit || submitting}>
            {t('保存')}
          </CustomButton>
        </FixedBottom>

      </div>
    </PageLayout>
  )
}

export { KycMode }
